import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { playAlertSound } from '../utils/alertSounds';

const NotificationContext = createContext();

// Helper to get sound settings from localStorage (shared with Settings.jsx)
const getSoundSettings = () => {
  try {
    const saved = localStorage.getItem('akb_sound_settings');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Error reading sound settings:', e);
  }
  // Default: enabled with 80% volume
  return { soundEnabled: true, alertVolume: 0.8 };
};

export function useNotifications() {
  return useContext(NotificationContext);
}

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [lastMilestoneCheck, setLastMilestoneCheck] = useState(null);
  const [lastRecoveryCheck, setLastRecoveryCheck] = useState(null);
  const [lastTokenCheck, setLastTokenCheck] = useState(null);

  // Get sound settings from localStorage (shared with Settings page)
  const initialSoundSettings = getSoundSettings();
  const [soundEnabled, setSoundEnabled] = useState(initialSoundSettings.soundEnabled);
  const [alertVolume, setAlertVolume] = useState(initialSoundSettings.alertVolume);

  // Listen for changes to sound settings from Settings page
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'akb_sound_settings') {
        const settings = getSoundSettings();
        setSoundEnabled(settings.soundEnabled);
        setAlertVolume(settings.alertVolume);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also check periodically in case localStorage is updated in the same tab
    const checkInterval = setInterval(() => {
      const settings = getSoundSettings();
      setSoundEnabled(settings.soundEnabled);
      setAlertVolume(settings.alertVolume);
    }, 5000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(checkInterval);
    };
  }, []);

  // Track which specific milestones/recoveries have been alerted (prevents duplicates)
  // Format: { "tokenAddress:multiplier": true } for milestones
  // Format: { "tokenAddress:milestone": true } for recoveries
  const [alertedMilestones, setAlertedMilestones] = useState(() => {
    try {
      const saved = localStorage.getItem('alertedMilestones');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  const [alertedRecoveries, setAlertedRecoveries] = useState(() => {
    try {
      const saved = localStorage.getItem('alertedRecoveries');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  // Individual notification type settings
  const [notificationSettings, setNotificationSettings] = useState(() => {
    const saved = localStorage.getItem('notificationSettings');
    return saved ? JSON.parse(saved) : {
      milestones: true,
      recovery: true,
      newTokens: true
    };
  });

  // Save alerted items to localStorage
  useEffect(() => {
    localStorage.setItem('alertedMilestones', JSON.stringify(alertedMilestones));
  }, [alertedMilestones]);

  useEffect(() => {
    localStorage.setItem('alertedRecoveries', JSON.stringify(alertedRecoveries));
  }, [alertedRecoveries]);

  const playMilestoneSound = useCallback(() => {
    if (!soundEnabled) return;
    playAlertSound('milestone', alertVolume).catch((err) => {
      console.error('Error playing milestone sound:', err);
    });
  }, [soundEnabled, alertVolume]);

  const playRecoveryDetectedSound = useCallback(() => {
    if (!soundEnabled) return;
    playAlertSound('recoveryDetected', alertVolume).catch((err) => {
      console.error('Error playing recovery detected sound:', err);
    });
  }, [soundEnabled, alertVolume]);

  const playRecoveryAlertSound = useCallback(() => {
    if (!soundEnabled) return;
    playAlertSound('recoveryAlert', alertVolume).catch((err) => {
      console.error('Error playing recovery alert sound:', err);
    });
  }, [soundEnabled, alertVolume]);

  const playNewTokenSound = useCallback(() => {
    if (!soundEnabled) return;
    playAlertSound('newToken', alertVolume).catch((err) => {
      console.error('Error playing new token sound:', err);
    });
  }, [soundEnabled, alertVolume]);

  const addNotification = useCallback((notification, isRepeat = false) => {
    // Check if this notification type is enabled
    if (notification.type === 'milestone' && !notificationSettings.milestones) return;
    if (notification.type === 'recovery' && !notificationSettings.recovery) return;
    if (notification.type === 'newToken' && !notificationSettings.newTokens) return;

    const id = Date.now() + Math.random();
    const newNotification = {
      ...notification,
      id,
      timestamp: new Date(),
      isRepeat: isRepeat
    };

    setNotifications(prev => [newNotification, ...prev].slice(0, 50)); // Keep last 50

    // Play appropriate sound based on notification type and subtype
    if (notification.type === 'milestone') {
      playMilestoneSound();
    } else if (notification.type === 'recovery') {
      // Check if this is a recovery detected or recovery milestone/alert
      if (notification.title?.includes('Started') || notification.title?.includes('Detected')) {
        playRecoveryDetectedSound();
      } else {
        playRecoveryAlertSound();
      }
    } else if (notification.type === 'newToken') {
      playNewTokenSound();
    }

    // Show browser notification if supported & permitted (iOS Safari has no Notification API)
    if (typeof window !== 'undefined' && 'Notification' in window && window.Notification.permission === 'granted') {
      try {
        const icons = {
          milestone: '🎯',
          recovery: '📈',
          newToken: '🪙'
        };
        new window.Notification(
          notification.title,
          {
            body: notification.message,
            icon: icons[notification.type] || '🔔',
            tag: `${notification.type}-${notification.id}`
          }
        );
      } catch (_) {
        /* notifications unsupported on this platform */
      }
    }
  }, [playMilestoneSound, playRecoveryDetectedSound, playRecoveryAlertSound, playNewTokenSound, notificationSettings]);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const updateNotificationSettings = useCallback((settings) => {
    setNotificationSettings(settings);
    localStorage.setItem('notificationSettings', JSON.stringify(settings));
  }, []);

  // Check for new milestones
  const checkMilestones = useCallback(async () => {
    try {
      const response = await api.get('/analytics/milestones?limit=5');
      const milestones = response.data.milestones;

      if (milestones.length === 0) return;

      // Get the latest milestone
      const latest = milestones[0];
      const latestTimestamp = new Date(latest.lastUpdatedAt).getTime();

      // First run - set timestamp but don't notify
      if (!lastMilestoneCheck) {
        setLastMilestoneCheck(latestTimestamp);
        return;
      }

      if (latestTimestamp > lastMilestoneCheck) {
        // Check all milestones for any that we haven't alerted yet
        for (const token of milestones) {
          if (token.milestones && token.milestones.length > 0) {
            for (const milestone of token.milestones) {
              const milestoneKey = `${token.tokenAddress}:${milestone.multiplier}`;

              // Only notify if not already alerted for this specific milestone
              if (!alertedMilestones[milestoneKey]) {
                addNotification({
                  type: 'milestone',
                  title: `🎯 ${milestone.multiplier}x Milestone!`,
                  message: `${token.tokenSymbol} reached ${milestone.multiplier}x market cap!`,
                  tokenSymbol: token.tokenSymbol,
                  tokenAddress: token.tokenAddress,
                  dexScreenerUrl: token.dexScreenerUrl,
                  data: milestone
                });
                // Mark this milestone as alerted
                setAlertedMilestones(prev => ({ ...prev, [milestoneKey]: true }));
              }
            }
          }
        }
        setLastMilestoneCheck(latestTimestamp);
      }
    } catch (error) {
      console.error('Error checking milestones:', error);
    }
  }, [lastMilestoneCheck, addNotification, alertedMilestones]);

  // Check for new tokens
  const checkNewTokens = useCallback(async () => {
    try {
      const response = await api.get('/analytics/tokens/recent?limit=5');
      const tokens = response.data.tokens;

      if (tokens.length === 0) return;

      const now = Date.now();

      // Only check tokens added in the last 5 minutes (to avoid old tokens on first load)
      const recentTokens = tokens.filter(token => {
        const tokenTime = new Date(token.createdAt).getTime();
        const timeDiff = now - tokenTime;
        return timeDiff < 5 * 60 * 1000; // 5 minutes
      });

      if (recentTokens.length === 0) {
        // No recent tokens, but update check time to avoid checking old tokens
        if (tokens.length > 0 && !lastTokenCheck) {
          setLastTokenCheck(new Date(tokens[0].createdAt).getTime());
        }
        return;
      }

      // Get the most recently added token from recent tokens
      const latest = recentTokens[0];
      const latestTimestamp = new Date(latest.createdAt).getTime();

      // Only notify if this is genuinely new (after our last check)
      if (!lastTokenCheck) {
        // First run - set the timestamp but don't notify for existing tokens
        setLastTokenCheck(latestTimestamp);
        return;
      }

      if (latestTimestamp > lastTokenCheck) {
        addNotification({
          type: 'newToken',
          title: `🪙 New Token Tracked!`,
          message: `${latest.name} (${latest.symbol}) is now being tracked`,
          tokenSymbol: latest.symbol,
          tokenAddress: latest.address,
          dexScreenerUrl: `https://dexscreener.com/${latest.chain}/${latest.address}`,
          data: latest
        });
        setLastTokenCheck(latestTimestamp);
      }
    } catch (error) {
      console.error('Error checking new tokens:', error);
    }
  }, [lastTokenCheck, addNotification]);

  // Check for new recoveries
  const checkRecoveries = useCallback(async () => {
    try {
      const response = await api.get('/analytics/recovery-alerts?limit=5');
      const recoveries = response.data.alerts || [];

      if (recoveries.length === 0) return;

      const latest = recoveries[0];
      const latestTimestamp = new Date(latest.alertSentAt).getTime();

      // First run - set timestamp but don't notify
      if (!lastRecoveryCheck) {
        setLastRecoveryCheck(latestTimestamp);
        return;
      }

      if (latestTimestamp > lastRecoveryCheck) {
        for (const recovery of recoveries) {
          const alertKey = recovery.milestone != null
            ? `${recovery.tokenAddress}:${recovery.milestone}`
            : `${recovery.tokenAddress}:start:${new Date(recovery.alertSentAt).getTime()}`;

          if (alertedRecoveries[alertKey]) continue;

          if (recovery.alertType === 'milestone' && recovery.milestone != null) {
            addNotification({
              type: 'recovery',
              title: `📈 Recovery: +${recovery.milestone}%!`,
              message: `${recovery.tokenSymbol} recovered ${recovery.milestone}% from bottom!`,
              tokenSymbol: recovery.tokenSymbol,
              tokenAddress: recovery.tokenAddress,
              dexScreenerUrl: recovery.dexScreenerUrl,
              data: recovery
            });
            setAlertedRecoveries(prev => ({ ...prev, [alertKey]: true }));
          } else if (recovery.alertType === 'start') {
            addNotification({
              type: 'recovery',
              title: `📈 Recovery Started!`,
              message: `${recovery.tokenSymbol} is recovering (+${recovery.recoveryPercentage?.toFixed(1) || '?'}% from bottom)`,
              tokenSymbol: recovery.tokenSymbol,
              tokenAddress: recovery.tokenAddress,
              dexScreenerUrl: recovery.dexScreenerUrl,
              data: recovery
            });
            setAlertedRecoveries(prev => ({ ...prev, [alertKey]: true }));
          }
        }
        setLastRecoveryCheck(latestTimestamp);
      }
    } catch (error) {
      console.error('Error checking recoveries:', error);
    }
  }, [lastRecoveryCheck, addNotification, alertedRecoveries]);

  // Poll for updates every 30 seconds
  useEffect(() => {
    // Request notification permission (skip on platforms without Notification API, e.g. iOS Safari)
    if (typeof window !== 'undefined' && 'Notification' in window) {
      try {
        if (window.Notification.permission === 'default') {
          window.Notification.requestPermission();
        }
      } catch (_) {
        /* unsupported / blocked */
      }
    }

    // Initial checks
    checkMilestones();
    checkRecoveries();
    checkNewTokens();

    // Set up polling
    const interval = setInterval(() => {
      checkMilestones();
      checkRecoveries();
      checkNewTokens();
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [checkMilestones, checkRecoveries, checkNewTokens]);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    soundEnabled,
    setSoundEnabled,
    notificationSettings,
    updateNotificationSettings,
    unreadCount: notifications.filter(n => !n.read).length
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
